import { useEffect, useMemo, useState } from "react";
import { PermissionCategory } from "../../http/protocol";
import { AdminUserService, DailyBudgetType } from "../../services/AdminUserService";
import { getContainer } from "../../ioc/IOCSetup";
import { DeleteButtonModal } from "../../util/DeleteButtonModal";
import { PermissionRequired } from "../security/PermissionRequired";
import { t } from "../../i18n";
import { HttpError } from "../../http/Types";

export interface AdminUserDailyBudgetProps {
  userId?: number;
}

export function AdminUserDailyBudget({ userId }: AdminUserDailyBudgetProps) {
  const [dailyBudget, setDailyBudget] = useState<DailyBudgetType>({});
  const hasNoDailySpend = !dailyBudget.dailySpentAmount;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const service: AdminUserService = useMemo(() => getContainer().getAdminUserService(), []);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    setIsLoading(true);
    (async () => {
      try {
        const budget = await service.getDailyBudget(userId);
        if (!cancelled) {
          setDailyBudget(budget);
          setError(undefined);
        }
      } catch {
        if (!cancelled) setError(t("label.couldNotLoadBudget"));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, service]);

  async function clearDailySpent() {
    let newBudget;
    if (!userId) return;
    setIsLoading(true);
    try {
      newBudget = await service.clearDailySpentAmount(userId);
    } catch (error) {
      const httpError = error as HttpError;
      if (httpError.status === 409) {
        setError(httpError.message);
      } else {
        setError(t("label.couldNotClearSpentBudget"));
      }
    } finally {
      if (newBudget) {
        setDailyBudget(newBudget);
        setError(undefined);
      }
      setIsLoading(false);
    }
  }

  return (
    <PermissionRequired
      permissions={[PermissionCategory.SUPERVISOR]}
      accessLevel="w"
    >
      <div className={"card"}>
        <div className={"card-body"}>
          <div className="row">
            <div className="col-lg-12">
              <div className="ibox float-e-margins">
                <div className="ibox-content">
                  <div className="ibox-title">
                    <h5>{t("title.dailyBudget")}</h5>
                  </div>
                  <div>
                    <div className="form-group mt-3">
                      <div className="col-sm-10 col-sm-offset-2">
                        <p>
                          <strong>{t("label.currentLimit")}: </strong>
                          {dailyBudget?.permissionLimit?.dailyLimit != null
                            ? `${dailyBudget.permissionLimit.dailyLimit.toLocaleString()} € (${dailyBudget.permissionLimit.permissionCategory})`
                            : t("label.noLimitSet")}
                        </p>
                        <p>
                          <strong>{t("label.spentToday")}: </strong>
                          {dailyBudget?.dailySpentAmount != null ? `${dailyBudget.dailySpentAmount.toLocaleString()} €` : "0 €"}
                        </p>
                        {error && <div className={"alert alert-danger"}>{error}</div>}
                        <DeleteButtonModal
                          className="btn btn-danger waves-effect waves-light"
                          onDelete={() => clearDailySpent()}
                          label={t("button.clearSpentBudget")}
                          description={`. ${t("confirm.clearDailySpentBudget")}`}
                          disabled={isLoading || hasNoDailySpend}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PermissionRequired>
  );
}
